import { useQuery } from "@tanstack/react-query";
import type { Session as SupabaseSession } from "@supabase/supabase-js";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/api/supabase";
import { getAppClaims } from "@/api/claims";
import { getCompanyById, type Company } from "@/api/companies";
import { currentHost } from "@/app/tenant";
import { setSignInNotice } from "@/app/sign-in-notice";
import { resolveAccess } from "@/domain/auth/access";
import { qk } from "@/api/queryKeys";

/**
 * App-wide session state: `unauthenticated | loading | admin |
 * companyUser(Company) | driver | super_admin`.
 *
 * Role, tenant and company come from the claims the access-token hook adds
 * to the token (`src/api/claims.ts`), not from per-login queries. A
 * "company" role additionally needs its active company record — no record
 * is treated the same as `unauthenticated` (a company that was archived
 * loses access immediately). `super_admin` (a platform admin on the
 * platform host) has no tenant data.
 */
export type AppSession =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | { status: "super_admin"; email: string }
  /** `readOnly`: a platform admin looking at an organization in a support session (changes nothing). */
  | { status: "admin"; email: string; readOnly?: true }
  | { status: "company"; company: Company; email: string }
  | { status: "driver"; email: string };

const SessionContext = createContext<AppSession | undefined>(undefined);

/** Raw Supabase auth session. `undefined` = not yet resolved on boot. */
function useSupabaseSession() {
  const [session, setSession] = useState<SupabaseSession | null | undefined>(
    undefined,
  );

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  return session;
}

const WRONG_TENANT_NOTICE =
  "That account belongs to a different organization. Open your own organization's address to sign in.";

async function resolveSession(userEmail: string | undefined): Promise<AppSession> {
  try {
    if (!userEmail) {
      return { status: "unauthenticated" };
    }

    const access = resolveAccess(await getAppClaims(), currentHost());

    switch (access.kind) {
      case "none":
        return { status: "unauthenticated" };
      case "wrong_tenant":
        setSignInNotice(WRONG_TENANT_NOTICE);
        await supabase.auth.signOut();
        return { status: "unauthenticated" };
      case "super_admin":
        return { status: "super_admin", email: userEmail };
      case "member": {
        if (access.role === "company") {
          if (access.companyId === null) {
            return { status: "unauthenticated" };
          }
          // throws when the company is archived or gone, which resolves to unauthenticated below
          const company = await getCompanyById(access.companyId);
          return { status: "company", company, email: userEmail };
        }
        return { status: access.role, email: userEmail };
      }
    }
  } catch {
    // Any resolution failure (network blip, RLS denial, etc.) is treated as
    // unauthenticated rather than surfaced as an error state.
    return { status: "unauthenticated" };
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const supabaseSession = useSupabaseSession();
  const userId = supabaseSession?.user.id;
  const userEmail = supabaseSession?.user.email;

  const roleQuery = useQuery({
    queryKey: [...qk.session(), userId],
    queryFn: () => resolveSession(userEmail),
    enabled: userId !== undefined,
    staleTime: 60_000,
  });

  const value: AppSession =
    supabaseSession === undefined
      ? { status: "loading" }
      : supabaseSession === null
        ? { status: "unauthenticated" }
        : (roleQuery.data ?? { status: "loading" });

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

/** Replaces the session for a subtree (support mode shows an organization's app as its admin, read-only). */
export function SessionOverride({ value, children }: { value: AppSession; children: ReactNode }) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): AppSession {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return ctx;
}
